import { Inject, Injectable } from '@angular/core';
import { AUTO_MESSAGE, ENVIRONMENT, GATE_CONSTANTS, HELP_OPTION_CODES, LANGUAGE, LANGUAGES_SUPPORTED, LOCALE_ID, PARAMS, ROUTED_DEPARTMENT, ROUTING_CODES, STORAGE_KEYS } from 'src/app/_constants/app.constants';
import { formatDate } from '@angular/common';
import { DepartmentRoutingService } from 'src/app/_services/api/department-routing/department-routing.service';
import { HelpOptionsService } from 'src/app/_services/api/help-options/help-options.service';
import { FlightService } from 'src/app/_services/api/flight/flight.service';
import { LanguageService } from 'src/app/_services/api/language/language.service';
import { SessionService } from 'src/app/_services/session/session.service';
import { AuthService } from 'src/app/_services/api/auth/auth.service';
import { SESSION_STORAGE_SERVICE } from 'src/app/_services/session/session.service';
import { StorageService } from 'ngx-webstorage-service';
import { ServiceModule } from 'src/app/_services/service.module';
import { Temporal } from '@js-temporal/polyfill';

@Injectable({
  providedIn: ServiceModule,
})
export class SessionUtilityService {

  travelerDetails: any = null;
  flightDetails: any = null;
  departmentRoutingDetails: any = null;
  activeFlightSegment: any = null;
  isBoardingPassFlow: Function;// UAVA-1650 - to use the boarding-pass reason from param as a seperate flow

  constructor(
    @Inject(SESSION_STORAGE_SERVICE) public storage: StorageService,
    private sessionService: SessionService,
    private flightService: FlightService,
    private languageService: LanguageService,
    private departmentRoutingService: DepartmentRoutingService,
    private authService: AuthService,
    private helpOptionsService: HelpOptionsService,
  ) {
    this.sessionService.setLanguage(this.languageService.currentLanguage);
    this.isBoardingPassFlow = this.sessionService.isBoardingPassFlow(this.sessionService.locationData.reason);
  }

  /**
   * Configures required data in session Service
   * 
   */
  configureSessionData() {
    this.travelerDetails = this.flightService.activeTraveler;
    this.flightDetails = this.flightService.flightDetails;
    this.departmentRoutingDetails = this.departmentRoutingService.departmentRoutingDetails;
    this.activeFlightSegment = this.flightService.activeSegment;
    this.resetRoutingCodeRC();
    this.setFlightInformationInSession();
    this.setTravelerDetailsInSession();
  }

  /**
   * Returns true if reason is valid else false;
   * Checks if the reason parameter is valid for the given PNR or NON_PNR flows
   * UAVA-1451 - reason should be accepted for case-insensitive values
   */
  isReasonParamValid(reason: any = this.sessionService.locationData.reason) {
    if (!reason) return false;
    let { options, secondary } = this.helpOptionsService?.helpOptions;
    return (options.includes(reason.toLowerCase() || (reason.toLowerCase()) == HELP_OPTION_CODES.OTHER && secondary)) ? true : false;
  }

  /**
   * UAVA-1497 -- show boarding pass when reason = boarding pass
   */
  isReasonToShowBoardingPass = (reason: string) => {
    let isValidReason = true;
    if (HELP_OPTION_CODES.BOARDING_PASS == reason) {
      this.flightService.showBoardingPass = true;
      isValidReason = false; // reasonValid changed to false, to open help-options page
    }
    return isValidReason;
  };

  /**
   * resetRoutingCodeRC : resets the routing code in the session service
   */
  resetRoutingCodeRC() {
    this.sessionService.locationData['routed-department-id'] = '';
    this.sessionService.locationData['routing-code'] = '';
    this.sessionService.setRegion('');
    const params = this.storage.get(STORAGE_KEYS.PARAMS);
    if (params) {
      if (params['rc']) {
        this.sessionService.locationData['routing-code'] = params['rc'];
      }
    }

    if (this.storage.has(STORAGE_KEYS.PARAMS)) {
      const params = this.storage.get(STORAGE_KEYS.PARAMS);
      if (params[PARAMS.ROUTING_CODE] == ROUTING_CODES.DOC.CODE) {
        this.sessionService.locationData['routing-code'] = ROUTING_CODES.DOC.CODE;
      }
    }
    this.setRoutingCodeRC();
  }

  /**
   * Set routing code RC - helps in setting required department in webex
   */
  setRoutingCodeRC() {
    // When rs = doc-check -- we set the routing code to 'DOC'
    if (this.sessionService.locationData.reason == HELP_OPTION_CODES.DOC_CHECK) {
      this.sessionService.locationData['routing-code'] = ROUTING_CODES.DOC.CODE;
      let docCheckRegion = this.flightService.flightDetails?.docCheckRegion || '';
      docCheckRegion && this.sessionService.setRegion(docCheckRegion);
    }
  }

  /**
   * setTravelerDetailsInSession : Set the travelers details from flight service to session service
   */
  setTravelerDetailsInSession() {
    this.sessionService.clearUserName();
    !this.sessionService.isEasyPnrSearch && this.sessionService.clearUserData(); // since we have phone added in additional info
    if (this.travelerDetails != null) {
      this.sessionService.userData.firstName = this.travelerDetails.givenName;
      this.sessionService.userData.lastName = this.travelerDetails.surname;
    }
  }

  /**
   * setFlightInformationInSession: set up flight data in session service location data
   */
  setFlightInformationInSession() {
    this.sessionService.setIsCheckedIn(this.flightService.activeTraveler?.isCheckedIn);
    const pnr = this.flightDetails?.confirmationID || '';
    this.sessionService.setPNR(pnr);
    const priorityScore = (this.departmentRoutingDetails?.score || (this.flightDetails?.score || '0')).toString();
    this.sessionService.setPriorityScore(priorityScore);
    const keyAttributes = this.departmentRoutingDetails?.keyAttributes || (this.flightDetails?.keyAttributes || '');
    this.sessionService.setKeyAttributes(keyAttributes);
    const flightNumber = (this.activeFlightSegment?.flightNumber || '').toString();
    this.sessionService.setFlightNumber(flightNumber);
    let departureDate = this.activeFlightSegment?.departureDateTime;
    const localSchDeptDtm = this.activeFlightSegment?.localSchDeptDtm;
    departureDate = localSchDeptDtm ? localSchDeptDtm : departureDate;
    departureDate = departureDate ? formatDate(departureDate, 'MM-dd-yyyy', LOCALE_ID) : '';
    let departureMinutes = this.activeFlightSegment?.departureDateTime ? this.sessionService.setTimeDiff(this.activeFlightSegment?.departureDateTime) : '';


    let estDepartureDate = this.activeFlightSegment?.departureDateTime;
    estDepartureDate = estDepartureDate ? formatDate(estDepartureDate, 'MM-dd-yyyy', LOCALE_ID) : '';

    let userCurrentDate = Temporal.Now.instant().toString();
    const currentDate = userCurrentDate ? formatDate(userCurrentDate, 'MM-dd-yyyy', LOCALE_ID) : '';
    this.sessionService.setDepartureDate(departureDate);
    const destinationAirport = this.activeFlightSegment?.arrivalAirport?.iataCode || '';
    this.sessionService.setDestinationAirport(destinationAirport);
    const originAirport = this.activeFlightSegment?.departureAirport?.iataCode || '';
    this.sessionService.setOriginAirport(originAirport);
    if (!this.sessionService.isLocationAvailableInParams) {
      let airportCode = ['NOT_DEPARTED', 'CANCEL', 'RETURNED_TO_GATE', 'DEPARTED_GATE', 'RETURNED_TO_AIRPORT']
        .includes(this.activeFlightSegment?.legStatus) ? originAirport : destinationAirport;
      airportCode = this.activeFlightSegment?.effectiveHub || airportCode;
      this.sessionService.setAirportCode(airportCode);
    } else {
      let airportCode = this.activeFlightSegment?.effectiveHub || false;
      if (airportCode) { this.sessionService.setAirportCode(airportCode); }
    }
    let flightStatus = this.activeFlightSegment?.flightStatus?.className || '';
    this.sessionService.setFlightStatus(flightStatus);
    let activeTravelerMpNumber = this.travelerDetails?.mpNumber || '';
    this.sessionService.setmpNumber(activeTravelerMpNumber);
    this.sessionService.setMpStatus(this.travelerDetails?.mpType);
    this.sessionService.setCallId(this.travelerDetails?.callId);
    this.sessionService.setSameDayFlight(estDepartureDate, currentDate);
    this.sessionService.setFlightConnectionTime(departureMinutes);
    if (this.flightDetails?.data.activateTravelPlan.ssRs && this.flightDetails?.data.activateTravelPlan.ssRs.length > 0) {
      this.sessionService.setSSR(this.flightDetails?.data.activateTravelPlan.ssRs);
    }

  }

  /**
   * Set the required data from the routing API to the session service.
   * Seperated since Routing call happens after pre-chat page.
   */

  setDepartmentRoutingDetailsInSession() {

    let airportCode = this.sessionService.locationData["airport-code"];
    airportCode = ((airportCode === ENVIRONMENT.PROPERTIES.DEPARTMENTS.XYZ.NAME) && this.authService.isIAM) ? ENVIRONMENT.PROPERTIES.DEPARTMENTS.GENERAL_IAM.NAME : airportCode;

    const departmentId = this.authService.isIAM ? ENVIRONMENT.PROPERTIES.DEPARTMENTS.GENERAL_IAM.ID.toString() : ENVIRONMENT.PROPERTIES.DEPARTMENTS.XYZ.ID.toString();

    this.sessionService.setCustomAutoMessage(this.languageService.constants?.autoMessage[AUTO_MESSAGE.DEFAULT])
    this.sessionService.setRoutedDepartmentId(departmentId)

    if (this.languageService.currentLanguage === LANGUAGE.SPANISH) {
      let department = ENVIRONMENT.PROPERTIES.DEPARTMENTS[LANGUAGES_SUPPORTED[this.languageService.currentLanguage].key.toUpperCase()];
      this.sessionService.setRoutedDepartmentId(department.ID.toString());
    }

    if ([GATE_CONSTANTS.TEST, GATE_CONSTANTS.Z200].includes(this.sessionService.locationData['gate-number'])) {
      this.sessionService.setCustomAutoMessage(this.languageService.constants?.autoMessage[AUTO_MESSAGE.TEST])
      this.sessionService.setRoutedDepartmentId(ENVIRONMENT.PROPERTIES.DEPARTMENTS.TEST.ID.toString())
    }

    if (
      this.sessionService.locationData['gate-number'] == GATE_CONSTANTS.BAG
      || this.sessionService.locationData.reason.toString() == HELP_OPTION_CODES.BAGGAGE
    ) {
      if (this.sessionService.locationData['routed-department-id'].toString() != ENVIRONMENT.PROPERTIES.DEPARTMENTS.BAGGAGE_BRC.ID.toString()) {
        this.sessionService.setCustomAutoMessage(this.languageService.constants?.autoMessage[AUTO_MESSAGE.DEFAULT]);
        this.sessionService.setRoutedDepartmentId(ENVIRONMENT.PROPERTIES.DEPARTMENTS.BAGGAGE.ID.toString());
      }
    }

    if (this.departmentRoutingService.departmentRoutingDetails) {

      this.departmentRoutingDetails = this.departmentRoutingService.departmentRoutingDetails;
      const priorityScore = (this.departmentRoutingDetails?.score || (this.flightDetails?.score || '0')).toString();
      this.sessionService.setPriorityScore(priorityScore);
      const keyAttributes = this.departmentRoutingDetails?.keyAttributes || (this.flightDetails?.keyAttributes || '');
      this.sessionService.setKeyAttributes(keyAttributes);
      const routedDepartmentId = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.routedDepartmentId || '';
      this.sessionService.setRoutedDepartmentId(routedDepartmentId);
      const customAutoMessage = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.autoMessage || '';
      this.sessionService.setCustomAutoMessage(customAutoMessage);

    }

    if (this.sessionService.locationData['airport-code'] == ENVIRONMENT.PROPERTIES.DEPARTMENTS.TRAIN.NAME) {
      this.sessionService.setCustomAutoMessage(this.languageService.constants?.autoMessage[AUTO_MESSAGE.DEFAULT]);
      this.sessionService.setRoutedDepartmentId(ENVIRONMENT.PROPERTIES.DEPARTMENTS.TRAIN.ID.toString());
    }

    if (
      this.sessionService.locationData['airport-code'] == ENVIRONMENT.PROPERTIES.DEPARTMENTS.VIP.NAME
      && this.sessionService.locationData['gate-number'] == GATE_CONSTANTS.VIP
    ) {
      this.sessionService.setCustomAutoMessage(this.languageService.constants?.autoMessage[AUTO_MESSAGE.DEFAULT]);
      this.sessionService.setRoutedDepartmentId(ENVIRONMENT.PROPERTIES.DEPARTMENTS.VIP.ID.toString());
    }

    if (this.sessionService.locationData.reason == HELP_OPTION_CODES.DOC_CHECK) {

      let routingDepartmentId = '';
      const routedDepartment = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.routedDepartment?.toLowerCase()

      switch (routedDepartment) {
        case ROUTED_DEPARTMENT.PREMIER:
          routingDepartmentId = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.routedDepartmentId;
          break;
        case ROUTED_DEPARTMENT.PREMIER_ONLY:
          routingDepartmentId = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.routedDepartmentId;
          break;
        case ROUTED_DEPARTMENT.SPANISH:
          routingDepartmentId = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.routedDepartmentId;
          break;
        case ROUTED_DEPARTMENT.SPANISH_ONLY:
          routingDepartmentId = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.routedDepartmentId;
          break;
        case ROUTED_DEPARTMENT.GENERAL:
          routingDepartmentId = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.routedDocCheckDepartmentId;
          break;
        case ROUTED_DEPARTMENT.GENERAL_ONLY:
          routingDepartmentId = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.routedDocCheckDepartmentId;
          break;
        default:
          routingDepartmentId = this.departmentRoutingDetails?.routedDepartmentResponse?.response?.routedDocCheckDepartmentId || ENVIRONMENT.PROPERTIES.DEPARTMENTS.DOC_CHECK.ID.toString();
      }

      this.sessionService.setCustomAutoMessage(this.languageService.constants?.autoMessage[AUTO_MESSAGE.DOC_CHECK] + '<br><br>' + this.languageService.constants?.autoMessage[AUTO_MESSAGE.DEFAULT]);
      this.sessionService.setRoutedDepartmentId(routingDepartmentId);

    }

    if (this.sessionService.locationData['routed-department-id'] == ENVIRONMENT.PROPERTIES.DEPARTMENTS.HAWAII_DOC_CHECK.ID.toString()) {
      this.sessionService.setCustomAutoMessage(this.languageService.constants?.autoMessage[AUTO_MESSAGE.DEFAULT]);
      this.sessionService.setRoutedDepartmentId(ENVIRONMENT.PROPERTIES.DEPARTMENTS.XYZ.ID.toString());
    }

  }

}
